import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { StateStorageService } from '@df/browser/storage/state-storage.service';
import { DfImageWrapperComponent } from '@df/module-image/df-image-wrapper.component';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentField } from '@icms/core/content';
import { signInAccessPrevUrlStorageKey } from '../guards/pwa-customer-logged.guard';
import { Catalog } from '@df/catalog/catalog.interfaces';

export namespace NSignInAccessLayout {
    export const componentId = 'sign-in-access-layout';

    export type IBlockData = Catalog.IBlockData<ISignInAccessLayoutContent>;

    export interface ISignInAccessLayoutContent {
        image: string;
        title: string;
        loginButtonText: string;
        skipButtonText: string;
        darkContrast: boolean;
        blur: boolean;
        overlayOpacity: number;
    }
}

@Component({
    selector: 'sign-in-access-layout',
    templateUrl: './sign-in-access-layout.component.html',
    styleUrls: ['./sign-in-access-layout.component.scss'],
    standalone: true,
    imports: [CommonModule, RouterModule, ICmsClientModule, DfImageWrapperComponent, DfImageDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<SignInAccessLayoutComponent>({
    id: NSignInAccessLayout.componentId,
    block: {
        url: NSignInAccessLayout.componentId
    },
    onDemandOnly: true,
    name: 'Sign In Access View',
    fields: [
        {
            type: NICmsContentField.EFieldType.image,
            key: 'image',
            title: 'Image',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.textarea,
            key: 'title',
            title: 'Title',
            default: 'Sign in and starting enjoying the perks of our Community',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'loginButtonText',
            title: 'Login Button Text',
            default: 'Sign In',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'skipButtonText',
            title: 'Skip Button Text',
            default: 'Not Now',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.toggle,
            key: 'darkContrast',
            title: 'Dark Contrast',
            default: true
        },
        {
            type: NICmsContentField.EFieldType.toggle,
            key: 'blur',
            title: 'Blur',
            default: false
        },
        <NICmsContentField.ISliderField>{
            type: NICmsContentField.EFieldType.slider,
            key: 'overlayOpacity',
            title: 'Overlay Opacity',
            default: 0.25,
            min: 0,
            max: 1,
            step: 0.05,
            required: true
        }
    ]
})
export class SignInAccessLayoutComponent extends UiComponent {
    protected prevUrl: string | undefined;
    private readonly stateStorageService = inject(StateStorageService);

    @ICmsContent({
        type: 'blockContent'
    })
    protected content?: NSignInAccessLayout.ISignInAccessLayoutContent;

    override ngOnInit(): void {
        super.ngOnInit();
        this.prevUrl = this.stateStorageService.get(signInAccessPrevUrlStorageKey) || '/';
    }
}
